import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import {
  Rocket,
  Clock,
  CheckCircle2,
  XCircle,
  RotateCcw,
  Filter,
  ChevronDown,
  ChevronRight,
  Trash2,
} from "lucide-react";

interface Deploy {
  deployId: string;
  deviceId: string;
  target: string;
  artifactKey?: string;
  version: string;
  repo: string;
  status: string;
  timestamp: string;
  createdAt?: string;
  completedAt?: string;
  error?: string;
}

interface GroupedDeploy {
  deployId: string;
  target: string;
  version: string;
  repo: string;
  timestamp: string;
  devices: Deploy[];
}

export function DeploysPage() {
  const [deploys, setDeploys] = useState<Deploy[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "pending" | "success" | "failed">("all");
  const [deleting, setDeleting] = useState<string | null>(null);

  const handleDeleteDeploy = async (deployId: string) => {
    if (!confirm("Remove this deploy record? This only removes the record — it does not affect devices.")) return;
    setDeleting(deployId);
    try {
      await api.delete(`/deploys/${deployId}`);
      setDeploys((prev) => prev.filter((d) => d.deployId !== deployId));
    } catch (err: any) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setDeleting(null);
    }
  };
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    api.get("/deploys").then(({ data }) => {
      setDeploys(data.deploys ?? []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Group deploys by deployId
  const grouped = deploys.reduce<Record<string, GroupedDeploy>>((acc, d) => {
    if (!acc[d.deployId]) {
      acc[d.deployId] = {
        deployId: d.deployId,
        target: d.target,
        version: d.version,
        repo: d.repo,
        timestamp: d.timestamp,
        devices: [],
      };
    }
    acc[d.deployId].devices.push(d);
    return acc;
  }, {});

  const groupedList = Object.values(grouped)
    .sort((a, b) => b.timestamp.localeCompare(a.timestamp));

  const filtered = filter === "all"
    ? groupedList
    : groupedList.filter((g) => g.devices.some((d) => d.status === filter));

  const getGroupStatus = (g: GroupedDeploy): string => {
    const statuses = g.devices.map((d) => d.status);
    if (statuses.every((s) => s === "success")) return "success";
    if (statuses.some((s) => s === "failed" || s === "rolled-back")) return "failed";
    if (statuses.some((s) => s === "in-progress")) return "in-progress";
    return "pending";
  };

  return (
    <div className="px-8 pt-8 pb-12">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-surface-900">Deploys</h1>
          <p className="mt-1 text-sm text-surface-500">
            {loading ? "Loading..." : `${groupedList.length} deploy${groupedList.length !== 1 ? "s" : ""}`}
          </p>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-1 bg-surface-0 border border-surface-200 rounded-lg p-1">
          {(["all", "pending", "success", "failed"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer ${
                filter === f
                  ? "bg-brand-100 text-brand-400"
                  : "text-surface-500 hover:text-surface-700 hover:bg-surface-100"
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {!loading && filtered.length === 0 && (
        <div className="mt-8 text-center py-16 bg-surface-0 rounded-xl border border-surface-200">
          <Rocket size={40} className="mx-auto text-surface-400 mb-4" strokeWidth={1.2} />
          <h2 className="text-lg font-medium text-surface-800">
            {filter === "all" ? "No deploys yet" : `No ${filter} deploys`}
          </h2>
          <p className="mt-2 text-sm text-surface-500">
            {filter === "all" ? "Trigger a deploy from the API or the Agent page." : "Try a different filter."}
          </p>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="mt-6 bg-surface-0 rounded-xl border border-surface-200 divide-y divide-surface-200">
          {filtered.map((group) => {
            const expanded = expandedId === group.deployId;
            const status = getGroupStatus(group);

            return (
              <div key={group.deployId}>
                <button
                  onClick={() => setExpandedId(expanded ? null : group.deployId)}
                  className="w-full px-5 py-4 flex items-center gap-4 hover:bg-surface-100/50 transition-colors cursor-pointer text-left"
                >
                  <div className="flex-shrink-0">
                    {expanded ? <ChevronDown size={16} className="text-surface-400" /> : <ChevronRight size={16} className="text-surface-400" />}
                  </div>

                  <StatusIcon status={status} />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-surface-900">
                        {group.target}
                      </span>
                      <span className="text-xs font-mono text-surface-500">
                        {group.version.slice(0, 8)}
                      </span>
                    </div>
                    <div className="text-xs text-surface-400 truncate">
                      {group.repo}
                    </div>
                  </div>

                  <div className="flex items-center gap-4 flex-shrink-0">
                    <span className="text-xs text-surface-400">
                      {group.devices.length} device{group.devices.length !== 1 ? "s" : ""}
                    </span>
                    <StatusBadge status={status} />
                    <span className="text-xs text-surface-400">
                      {new Date(group.timestamp).toLocaleString()}
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteDeploy(group.deployId); }}
                      disabled={deleting === group.deployId}
                      className="p-1.5 text-surface-400 hover:text-danger-500 hover:bg-danger-100 rounded transition-colors cursor-pointer disabled:opacity-50"
                      title="Remove deploy record"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </button>

                {expanded && (
                  <div className="px-5 pb-5 ml-[52px]">
                    <div className="text-[11px] text-surface-400 font-mono mb-3">
                      Deploy ID: {group.deployId}
                    </div>

                    <div className="bg-surface-50 rounded-lg border border-surface-200 divide-y divide-surface-200">
                      {group.devices.map((d) => (
                        <div key={d.deviceId}>
                        <div className="px-4 py-3 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <StatusIcon status={d.status} />
                            <span className="text-sm font-mono text-surface-700">{d.deviceId}</span>
                          </div>
                          <div className="flex items-center gap-4">
                            <StatusBadge status={d.status} />
                            {d.completedAt && (
                              <span className="text-xs text-surface-400">
                                {new Date(d.completedAt).toLocaleString()}
                              </span>
                            )}
                          </div>
                        </div>
                        {d.error && (
                          <div className="px-4 pb-3 -mt-1">
                            <div className="ml-[26px] p-2.5 rounded bg-danger-100 border border-danger-200">
                              <pre className="text-xs text-danger-500 whitespace-pre-wrap break-all font-mono leading-relaxed">{d.error}</pre>
                            </div>
                          </div>
                        )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatusIcon({ status }: { status: string }) {
  switch (status) {
    case "success":
      return <CheckCircle2 size={16} className="text-success-500 flex-shrink-0" />;
    case "failed":
      return <XCircle size={16} className="text-danger-500 flex-shrink-0" />;
    case "rolled-back":
      return <RotateCcw size={16} className="text-warning-500 flex-shrink-0" />;
    case "in-progress":
      return <Rocket size={16} className="text-brand-400 flex-shrink-0" />;
    default:
      return <Clock size={16} className="text-surface-400 flex-shrink-0" />;
  }
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    success: "bg-success-100 text-success-500",
    failed: "bg-danger-100 text-danger-500",
    "rolled-back": "bg-warning-100 text-warning-500",
    "in-progress": "bg-brand-100 text-brand-400",
    pending: "bg-surface-200 text-surface-500",
  };

  return (
    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${styles[status] || styles.pending}`}>
      {status}
    </span>
  );
}


